import React from "react";

interface CourseThumbnailIconProps {
  className?: string;
  size?: number;
}

/**
 * CourseThumbnailIcon Component
 * 
 * A beautiful SVG icon used as a fallback for course thumbnails.
 * Features a modern, educational design with book and graduation cap elements.
 */
export const CourseThumbnailIcon: React.FC<CourseThumbnailIconProps> = ({
  className = "",
  size = 200,
}) => {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 200 200"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      role="img"
      aria-label="Course thumbnail placeholder"
    >
      {/* Background gradient */}
      <defs>
        <linearGradient id="bgGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#667eea" />
          <stop offset="100%" stopColor="#764ba2" />
        </linearGradient>
        <linearGradient id="bookGradient" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#ffffff" />
          <stop offset="100%" stopColor="#f0f0f0" />
        </linearGradient>
      </defs>

      {/* Background */}
      <rect width="200" height="200" fill="url(#bgGradient)" />

      {/* Decorative circles */}
      <circle cx="30" cy="30" r="20" fill="rgba(255,255,255,0.1)" />
      <circle cx="170" cy="170" r="25" fill="rgba(255,255,255,0.1)" />
      <circle cx="180" cy="40" r="15" fill="rgba(255,255,255,0.08)" />

      {/* Open book */}
      <g transform="translate(50, 70)">
        {/* Left page */}
        <path
          d="M 0 0 Q 0 40 0 60 L 45 60 Q 50 50 50 40 L 50 0 Z"
          fill="url(#bookGradient)"
          stroke="#e0e0e0"
          strokeWidth="1"
        />
        {/* Right page */}
        <path
          d="M 50 0 L 50 40 Q 50 50 55 60 L 100 60 Q 100 40 100 0 Z"
          fill="url(#bookGradient)"
          stroke="#e0e0e0"
          strokeWidth="1"
        />
        {/* Book spine shadow */}
        <line
          x1="50"
          y1="0"
          x2="50"
          y2="60"
          stroke="#d0d0d0"
          strokeWidth="2"
        />
        {/* Page lines - left */}
        <line x1="10" y1="15" x2="40" y2="15" stroke="#c0c0c0" strokeWidth="1" />
        <line x1="10" y1="25" x2="40" y2="25" stroke="#c0c0c0" strokeWidth="1" />
        <line x1="10" y1="35" x2="35" y2="35" stroke="#c0c0c0" strokeWidth="1" />
        {/* Page lines - right */}
        <line x1="60" y1="15" x2="90" y2="15" stroke="#c0c0c0" strokeWidth="1" />
        <line x1="60" y1="25" x2="90" y2="25" stroke="#c0c0c0" strokeWidth="1" />
        <line x1="65" y1="35" x2="90" y2="35" stroke="#c0c0c0" strokeWidth="1" />
      </g>

      {/* Graduation cap */}
      <g transform="translate(70, 40)">
        {/* Cap top */}
        <path
          d="M 30 0 L 0 10 L 30 20 L 60 10 Z"
          fill="#fbbf24"
          stroke="#f59e0b"
          strokeWidth="1.5"
        />
        {/* Cap base */}
        <path
          d="M 25 12 L 25 22 Q 30 25 35 22 L 35 12"
          fill="#f59e0b"
          stroke="#d97706"
          strokeWidth="1"
        />
        {/* Tassel */}
        <circle cx="60" cy="10" r="3" fill="#dc2626" />
        <line x1="60" y1="10" x2="60" y2="18" stroke="#dc2626" strokeWidth="1.5" />
      </g>

      {/* Sparkle effects */}
      <g opacity="0.6">
        <path
          d="M 40 150 L 42 155 L 47 157 L 42 159 L 40 164 L 38 159 L 33 157 L 38 155 Z"
          fill="#fbbf24"
        />
        <path
          d="M 160 90 L 162 95 L 167 97 L 162 99 L 160 104 L 158 99 L 153 97 L 158 95 Z"
          fill="#fbbf24"
        />
        <path
          d="M 25 100 L 27 104 L 31 106 L 27 108 L 25 112 L 23 108 L 19 106 L 23 104 Z"
          fill="#ffffff"
        />
      </g>
    </svg>
  );
};

export default CourseThumbnailIcon;
